import { FloatingLoader } from "@/components";
import { cn } from "@/libraries/cn";
import { CASE_MEMBER_TYPE } from "@/libraries/constant";
import {
	getMembersQueryOptions,
	getMpisMembersQueryOptions,
	getTeamsQueryOptions,
} from "@/queries";
import type { Case, CaseTeam, ContactMemberResponse } from "@/types";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";
import Externals from "./Externals";

export interface ExternalInvitationsProps
	extends Omit<React.ComponentProps<"div">, "onChange"> {
	caseItem?: Case;
	search?: string;
	tab: "mpis" | "contact" | "directory";
	subTab: "team" | "contact";
	selectedTeamId?: number;
	selectedMemberId?: number | string | null;
	isMobile?: boolean;
	onChange: (
		e: React.ChangeEvent<HTMLInputElement>,
		val: any,
		type: "teams" | "contacts",
	) => void;
}

const ExternalInvitations = ({
	tab = "contact",
	search = "",
	subTab = "team",
	caseItem,
	onChange,
	selectedTeamId = 0,
	selectedMemberId = 0,
	isMobile = false,
}: ExternalInvitationsProps) => {
	const currentQueryOption = useMemo(
		() =>
			tab !== "mpis"
				? getMembersQueryOptions({
						directory: tab === "directory",
					})
				: getMpisMembersQueryOptions(),
		[tab],
	);

	const contactsQuery = useSuspenseInfiniteQuery(currentQueryOption as any);
	const teamsQuery = useSuspenseInfiniteQuery(
		getTeamsQueryOptions({
			directory: tab === "directory",
			scope: "case_teams",
		}),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const currentQuery = useMemo(
		() =>
			tab === "mpis"
				? contactsQuery
				: subTab === "contact"
					? contactsQuery
					: teamsQuery,
		[tab, subTab],
	);

	const alreadyInvitedTeams = useMemo(
		() =>
			caseItem
				? Array.from(
						new Set([
							...caseItem.pending_teams.map((i) => i.id),
							...caseItem.teams.map((i) => i.id),
						]),
					)
				: [],
		[caseItem],
	);

	const alreadyInvitedContacts = useMemo(
		() =>
			caseItem
				? uniqBy(
						[
							...caseItem.pending_invitations,
							...caseItem.external_pending_invitations,
							...caseItem.internal_members,
							...caseItem.external_members.filter(
								(member) =>
									member.member_type !==
									CASE_MEMBER_TYPE.EXTERNAL_REQUEST_MEMBER,
							),
						],
						(item) => item.id || item.email,
					)
				: [],
		[caseItem],
	);

	const teams = useMemo<CaseTeam[]>(
		() => teamsQuery.data.pages.flatMap((p) => p.teams ?? []) as CaseTeam[],
		[teamsQuery.data],
	);
	const contacts = useMemo(
		() => contactsQuery.data as unknown as ContactMemberResponse["users"],
		[contactsQuery.data],
	);

	const isLoading = useMemo(
		() =>
			contactsQuery.isLoading ||
			teamsQuery.isLoading ||
			contactsQuery.isFetchingNextPage ||
			teamsQuery.isFetchingNextPage,
		[
			contactsQuery.isLoading,
			teamsQuery.isLoading,
			contactsQuery.isFetchingNextPage,
			teamsQuery.isFetchingNextPage,
		],
	);

	const isEmpty = useMemo(
		() =>
			subTab === "contact"
				? !isLoading && !contacts.length
				: !isLoading && !teams.length,
		[subTab, isLoading, contacts, teams],
	);

	const listItems = useMemo(() => {
		const cleanedTeams = teams
			.filter((team) => !alreadyInvitedTeams.includes(team.id))
			.map((team) => ({
				...team,
				isChecked: team.id === selectedTeamId,
			}));

		if (subTab === "team" && !search) {
			return cleanedTeams;
		}

		if (subTab === "team" && search) {
			return cleanedTeams.filter((team) =>
				team.name?.toLowerCase().includes(search.toLowerCase()),
			);
		}

		const cleanedContacts = contacts
			.filter(
				(contact) =>
					!alreadyInvitedContacts.some(
						(invited) =>
							invited.id === contact.id || invited.email === contact.email,
					),
			)
			.map((member) => ({
				...member,
				isChecked: (member.id || member.aidbox_id) === selectedMemberId,
			}));

		if (subTab === "contact" && !search) {
			return cleanedContacts;
		}

		if (subTab === "contact" && search) {
			return cleanedContacts.filter((contact) =>
				contact.fullname?.toLowerCase().includes(search.toLowerCase()),
			);
		}

		return [];
	}, [
		subTab,
		teams,
		search,
		contacts,
		selectedTeamId,
		selectedMemberId,
		alreadyInvitedTeams,
		alreadyInvitedContacts,
	]);

	return (
		<div
			className={cn("relative flex-1 bg-white", {
				"overflow-y-hidden": !isMobile,
			})}
		>
			<FloatingLoader open={isLoading} />
			<Externals
				tab={tab}
				subTab={subTab}
				listItems={listItems}
				isEmpty={isEmpty}
				isLoading={isLoading}
				hasNextPage={currentQuery.hasNextPage}
				isFetchingNextPage={currentQuery.isFetchingNextPage}
				fetchNextPage={currentQuery.fetchNextPage}
				onChange={onChange}
			/>
		</div>
	);
};

export default ExternalInvitations;
